import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { addDays, todayIso } from "@/lib/format";

const STORAGE_KEY = "prodtrack_analyse_filtres";

const firstOfMonth = () => todayIso().slice(0, 8) + "01";
const firstOfQuarter = () => {
  const t = todayIso();
  const qm = Math.floor((Number(t.slice(5, 7)) - 1) / 3) * 3 + 1;
  return `${t.slice(0, 4)}-${String(qm).padStart(2, "0")}-01`;
};

export const PRESETS = [
  { key: "jour", label: "Aujourd'hui", range: () => ({ du: todayIso(), au: todayIso() }) },
  { key: "hier", label: "Hier", range: () => ({ du: addDays(todayIso(), -1), au: addDays(todayIso(), -1) }) },
  { key: "7j", label: "7 jours", range: () => ({ du: addDays(todayIso(), -6), au: todayIso() }) },
  { key: "30j", label: "30 jours", range: () => ({ du: addDays(todayIso(), -29), au: todayIso() }) },
  { key: "mois", label: "Mois en cours", range: () => ({ du: firstOfMonth(), au: todayIso() }) },
  { key: "trim", label: "Trimestre", range: () => ({ du: firstOfQuarter(), au: todayIso() }) },
  { key: "12m", label: "12 mois", range: () => ({ du: addDays(todayIso(), -364), au: todayIso() }) },
];

export const ONGLETS = [
  { key: "vue", label: "Vue d'ensemble" },
  { key: "production", label: "Production" },
  { key: "trs", label: "TRS / OEE" },
  { key: "arrets", label: "Arrêts" },
  { key: "rejets", label: "Rejets" },
  { key: "of", label: "Ordres de fabrication" },
];

export const EMPTY_FILTRES = {
  du: addDays(todayIso(), -29), au: todayIso(), preset: "30j",
  ligne_id: "", produit_id: "", famille: "", quart_id: "", equipe: "", superviseur: "",
  operateur_id: "", of_id: "", categorie_arret_id: "", type_arret: "", motif_rejet_id: "",
  statut_of: "", unite: "",
};

export const FILTRE_LABELS = {
  ligne_id: "Ligne", produit_id: "Produit", famille: "Famille", quart_id: "Quart",
  equipe: "Équipe", superviseur: "Superviseur", operateur_id: "Opérateur", of_id: "OF",
  categorie_arret_id: "Catégorie d'arrêt", type_arret: "Type d'arrêt",
  motif_rejet_id: "Motif de rejet", statut_of: "Statut OF", unite: "Unité",
};

const NUM = ["ligne_id", "produit_id", "quart_id", "operateur_id", "of_id", "categorie_arret_id", "motif_rejet_id"];
const TXT = ["famille", "equipe", "superviseur", "type_arret", "statut_of", "unite"];

/** Filtres PERSISTANTS, partagés par tous les onglets (localStorage + état commun). */
export const useAnalyseFiltres = () => {
  const [filtres, setFiltres] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return saved ? { ...EMPTY_FILTRES, ...saved } : EMPTY_FILTRES;
    } catch { return EMPTY_FILTRES; }
  });
  const update = useCallback((patch) => {
    setFiltres((f) => {
      const next = { ...f, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setFiltres(EMPTY_FILTRES);
  }, []);
  return { filtres, update, reset };
};

export const toParams = (f) => {
  const out = { du: f.du, au: f.au };
  NUM.forEach((k) => { if (f[k]) out[k] = Number(f[k]); });
  TXT.forEach((k) => { if (f[k]) out[k] = f[k]; });
  return out;
};

export const filtresActifs = (f) =>
  Object.keys(FILTRE_LABELS).filter((k) => f[k] !== "" && f[k] != null);

export const download = async (url, params, filename) => {
  const res = await api.get(url, { params, responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

/** « 18 000 kg · 195 pcs » — jamais un total unique quand les unités sont incompatibles. */
export const fmtQuantites = (par_unite, cle = "bonnes") =>
  !par_unite?.length ? "—"
    : par_unite.map((e) => `${Number(e[cle] ?? 0).toLocaleString("fr-FR")} ${e.unite}`).join(" · ");
